<script setup lang="ts">
import useVuelidate from '@vuelidate/core'
import { useVModel } from '@vueuse/core'

import type { Customer, Identification } from '~/layers/forms/models'
import { CustomerTypeEnum, Form, Insured } from '~/layers/forms/models'

interface Props {
  modelValue: Insured
  customer?: Customer
  customerIdentification?: Identification
  insurable?: Insurable
}

const props = defineProps<Props>()

const emits = defineEmits(['update:modelValue'])
const model = useVModel(props, 'modelValue', emits)
const { allowCustomerAsInsured } = useContractConstraints()
useVuelidate({}, model, {
  $scope: Form.insured,
  $registerAs: Form.insured,
})

if (allowCustomerAsInsured) model.value.customerAsInsured = true

const { RemoteConfigurationStore } = stores()
watch(
  computed(() => RemoteConfigurationStore.value?.form?.aseguradoEsContratante),
  (n) => {
    model.value.customerAsInsured = n ?? false
  },
  {
    immediate: true,
  }
)

watch(
  [computed(() => model.value.customerAsInsured), computed(() => props.customer)],
  (n, o) => {
    if (n[0] !== o[0]) model.value = model.value = Insured.load({ customerAsInsured: false })
    if (!n[0]) return

    model.value = Insured.load({
      ...props.customer,
      ...props.customerIdentification,
      customerAsInsured: true,
    })
  },
  {
    deep: true,
  }
)
</script>

<template>
  <FormsGlobalContainer
    v-slot="{ sectionClasses }"
    :identifier="Form.insured"
  >
    <FormsGlobalTitle
      text="Información del asegurado"
      icon-class="icon-[ph--person-bold]"
    />

    <InputContainer
      v-if="allowCustomerAsInsured"
      class="col-span-full"
    >
      <div class="flex justify-between items-center w-full">
        <div class="flex items-center gap-3">
          <PvCheckbox v-model="model.customerAsInsured" />
          <div>El contratante es el asegurado</div>
        </div>
      </div>
    </InputContainer>
    <div :class="[sectionClasses(), 'col-span-full']">
      <FormsIdentificationFields
        v-model="model.identification"
        :entity-type="CustomerTypeEnum.Person.code"
        :validation-scope="Form.insured"
        :register-validation-as="`${Form.insured}-identification`"
        :readonly="model.customerAsInsured ?? false"
      />
    </div>

    <FormsGlobalPersonBaseForm
      :model-value="model"
      :validation-scope="Form.insured"
      :insurable
      :prefill="model.customerAsInsured ? 'all' : false"
    >
      <slot name="aditional-fields" />
    </FormsGlobalPersonBaseForm>
  </FormsGlobalContainer>
</template>
